import { z } from 'zod';

import { ACTOR_RULES, STAGE_TONES } from '../domain/workflow.js';
import { TICKET_STATUSES } from '../domain/enums.js';
import { slugSchema, uuidSchema } from './common.js';
import { routingTargetInputSchema } from './board.js';

const labelSchema = z.string().trim().min(2).max(60);

export const createStageSchema = z
  .object({
    slug: slugSchema,
    label: labelSchema,
    tone: z.enum(STAGE_TONES).default('neutral'),
    resultingStatus: z.enum(TICKET_STATUSES).default('open'),
    isTerminal: z.boolean().default(false),
    isHidden: z.boolean().default(false),
    afterStageId: uuidSchema.nullable().default(null),
  })
  .strict();

export const updateStageSchema = z
  .object({
    label: labelSchema.optional(),
    tone: z.enum(STAGE_TONES).optional(),
    resultingStatus: z.enum(TICKET_STATUSES).optional(),
    isInitial: z.literal(true).optional(),
    isTerminal: z.boolean().optional(),
    isHidden: z.boolean().optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, 'Nothing to update.');

export const reorderStagesSchema = z
  .object({ stageIds: z.array(uuidSchema).min(1).max(50) })
  .strict();

export type CreateStageInput = z.infer<typeof createStageSchema>;
export type UpdateStageInput = z.infer<typeof updateStageSchema>;
export type ReorderStagesInput = z.infer<typeof reorderStagesSchema>;

const transitionBody = {
  slug: slugSchema,
  label: labelSchema,
  fromStageId: uuidSchema,
  toStageId: uuidSchema,
  actorRule: z.enum(ACTOR_RULES),
  actorDepartmentId: uuidSchema.nullable().default(null),
  forbidSelfService: z.boolean().default(false),
  handOffTarget: routingTargetInputSchema.nullable().default(null),
};

export const createTransitionSchema = z
  .object(transitionBody)
  .strict()
  .refine((value) => value.fromStageId !== value.toStageId, {
    message: 'A move must go from one column to a different one.',
    path: ['toStageId'],
  });

export const updateTransitionSchema = z
  .object({
    label: labelSchema.optional(),
    fromStageId: uuidSchema.optional(),
    toStageId: uuidSchema.optional(),
    actorRule: z.enum(ACTOR_RULES).optional(),
    actorDepartmentId: uuidSchema.nullable().optional(),
    forbidSelfService: z.boolean().optional(),
    handOffTarget: routingTargetInputSchema.nullable().optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, 'Nothing to update.');

export type CreateTransitionInput = z.infer<typeof createTransitionSchema>;
export type UpdateTransitionInput = z.infer<typeof updateTransitionSchema>;
